<template>
  <div class="lesson-view">
    <div class="lesson-navigation">
      <router-link
        v-if="previousLesson"
        :to="{ name: 'lesson', params: { chapterId: previousLesson.chapterId, lessonId: previousLesson.lessonId }}"
        class="nav-button prev"
      >
        Previous Lesson
      </router-link>
      <router-link
        v-if="nextLesson"
        :to="{ name: 'lesson', params: { chapterId: nextLesson.chapterId, lessonId: nextLesson.lessonId }}"
        class="nav-button next"
      >
        Next Lesson
      </router-link>
    </div>

    <div class="breadcrumb">
      <router-link to="/" class="breadcrumb-link">Home</router-link>
      <span class="breadcrumb-separator">/</span>
      <span class="breadcrumb-current">{{ chapterTitle }}</span>
      <span class="breadcrumb-separator">/</span>
      <span class="breadcrumb-current">{{ lessonTitle }}</span>
    </div>

    <component :is="currentLesson.component" v-if="currentLesson" />
  </div>
</template>

<script>
import { computed, onMounted, watch, ref, inject } from 'vue'
import { useRoute } from 'vue-router'
import { getLesson, getNextLesson, getPreviousLesson, getChapter } from '../content/chapters'
import { saveUserProgress, getLastCompletedLesson, parseLessonKey } from '../utils/progress'

export default {
  name: 'LessonView',
  setup() {
    const route = useRoute()
    // Keep track of the previously viewed lesson
    const previouslyViewedLesson = ref(null)
    // Inject the progress initialization state
    const progressInitialized = inject('progressInitialized')
    const logger = inject('logger')

    const currentLesson = computed(() => {
      const chapterId = parseInt(route.params.chapterId)
      const lessonId = parseInt(route.params.lessonId)
      return getLesson(chapterId, lessonId)
    })

    const nextLesson = computed(() => {
      const chapterId = parseInt(route.params.chapterId)
      const lessonId = parseInt(route.params.lessonId)
      return getNextLesson(chapterId, lessonId)
    })

    const previousLesson = computed(() => {
      const chapterId = parseInt(route.params.chapterId)
      const lessonId = parseInt(route.params.lessonId)
      return getPreviousLesson(chapterId, lessonId)
    })

    const chapterTitle = computed(() => {
      const chapterId = parseInt(route.params.chapterId)
      const chapter = getChapter(chapterId)
      return chapter ? chapter.title : ''
    })

    const lessonTitle = computed(() => {
      const chapterId = parseInt(route.params.chapterId)
      const lessonId = parseInt(route.params.lessonId)
      const lesson = getLesson(chapterId, lessonId)
      return lesson ? lesson.title : ''
    })

    // Function to determine if second lesson is further in the curriculum than first
    const isLessonFurtherThan = (lesson1, lesson2) => {
      if (!lesson1 || !lesson2) return false;
      
      return lesson2.chapterId > lesson1.chapterId || 
             (lesson2.chapterId === lesson1.chapterId && lesson2.lessonId > lesson1.lessonId);
    }
    
    // Track the current lesson
    const trackProgress = async () => {
      // Only track progress after initialization
      if (!progressInitialized.value) {
        logger.debug('LessonView: Waiting for progress to initialize...');
        return;
      }
      
      const currentChapterId = parseInt(route.params.chapterId)
      const currentLessonId = parseInt(route.params.lessonId)
      const currentLessonInfo = { chapterId: currentChapterId, lessonId: currentLessonId }
      
      // Get last saved progress
      const lastProgress = getLastCompletedLesson();
      const lastProgressInfo = lastProgress ? parseLessonKey(lastProgress) : null;
      
      logger.debug('Progress tracking details:', {
        currentLesson: currentLessonInfo,
        previouslyViewedLesson: previouslyViewedLesson.value,
        lastProgressFromAPI: lastProgressInfo
      });
      
      // When user first visits the site, set the previously viewed lesson
      if (!previouslyViewedLesson.value) {
        previouslyViewedLesson.value = currentLessonInfo;
        return;
      }
      
      // Case 1: User moved forward in the curriculum
      if (isLessonFurtherThan(previouslyViewedLesson.value, currentLessonInfo)) {
        // Only save the previous lesson if it's further than what's already been saved
        if (!lastProgressInfo || isLessonFurtherThan(lastProgressInfo, previouslyViewedLesson.value)) {
          logger.info('User moved forward - saving previous lesson');
          await saveUserProgress(previouslyViewedLesson.value.chapterId, previouslyViewedLesson.value.lessonId);
        } else {
          logger.debug('Previous lesson already saved or earlier than last saved progress');
        }
      } 
      // Case 2: User moved backward - no need to save anything
      else {
        logger.debug('User moved backward or stayed on same lesson - not saving');
      }
      
      // Update the previously viewed lesson for next navigation
      previouslyViewedLesson.value = currentLessonInfo;
    }

    onMounted(() => {
      logger.debug('LessonView component mounted');
      // Initialize the previously viewed lesson, but don't fetch progress
      // That's handled by App.vue
      previouslyViewedLesson.value = {
        chapterId: parseInt(route.params.chapterId),
        lessonId: parseInt(route.params.lessonId)
      };
    })
    
    // Watch for initialization
    watch(
      progressInitialized,
      (initialized) => {
        if (initialized) {
          trackProgress();
        }
      }
    )

    // Watch for route changes to track progress when navigating between lessons
    watch(
      () => route.params,
      () => {
        if (progressInitialized.value) {
          trackProgress();
        }
      },
      { deep: true }
    )

    return {
      currentLesson,
      nextLesson,
      previousLesson,
      chapterTitle,
      lessonTitle
    }
  }
}
</script>

<style scoped>
.lesson-view {
  max-width: 800px;
  margin: 0 auto;
  padding: 2rem;
}

.lesson-navigation {
  display: flex;
  justify-content: space-between;
  margin-bottom: 1rem;
}

.nav-button {
  padding: 0.5rem 1rem;
  border: 1px solid var(--border);
  border-radius: 4px;
  text-decoration: none;
  color: var(--text);
  transition: all 0.2s ease;
}

.nav-button:hover {
  background: var(--primary);
  color: white;
  border-color: var(--primary);
}

.prev {
  margin-right: auto;
}

.next {
  margin-left: auto;
}

.breadcrumb {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  margin-bottom: 2rem;
  color: var(--text-light);
  font-size: 0.95rem;
}

.breadcrumb-link {
  color: var(--primary);
  text-decoration: none;
  transition: color 0.2s ease;
}

.breadcrumb-link:hover {
  color: var(--primary-light);
}

.breadcrumb-separator {
  color: var(--text-light);
}

.breadcrumb-current {
  color: var(--text);
}
</style> 